import {
  DrawRectangleControls,
  useImmediateControlsLock,
  useLotvDispose,
} from "@faro-lotv/app-component-toolbox";
import { useCallback, useState, type JSX } from "react";
import {
  Color,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
} from "three";
import { MANUAL_BLUR_COLOR } from "./manual-blurring-constants";
import { MANUAL_BLUR_LABEL } from "./manual-blurring-types";

type CreateManualBlurPreviewProps = {
  /**
   * True to enable the tool
   *
   * @default true
   */
  isEnabled?: boolean;

  /** Callback executed when a blur has been created */
  onCreated(mesh: Mesh): void;
};

/**
 * @returns Controls that allow the user to draw manual blurs while active.
 * Also renders a preview of the currently drawn blur in the scene.
 */
export function CreateManualBlurPreview({
  isEnabled = true,
  onCreated,
}: CreateManualBlurPreviewProps): JSX.Element {
  const [currentBlur, setCurrentBlur] = useState<Mesh>();

  const [rectangle] = useState(() => {
    const material = new MeshBasicMaterial({
      color: new Color(MANUAL_BLUR_COLOR).getHex(),
      side: DoubleSide,
      depthTest: false,
    });
    const rectangleGeometry = new PlaneGeometry(1, 1);

    return new Mesh(rectangleGeometry, material);
  });

  const onBlurUpdated = useCallback((blur?: Mesh) => {
    if (blur) {
      blur.name = MANUAL_BLUR_LABEL;
    }
    setCurrentBlur(blur);
  }, []);

  const onBlurCreated = useCallback(
    (newBlur: Mesh) => {
      setCurrentBlur(undefined);
      onCreated(newBlur);
    },
    [onCreated],
  );

  useLotvDispose(currentBlur);

  const { lock, unlock } = useImmediateControlsLock();

  return (
    <>
      <DrawRectangleControls
        mesh={rectangle}
        onRectangleCreated={onBlurCreated}
        onRectangleUpdated={onBlurUpdated}
        onDragStart={lock}
        onDragEnd={unlock}
        isEnabled={isEnabled}
      />

      {currentBlur && <primitive object={currentBlur} />}
    </>
  );
}
